import type { ReactNode } from 'react';
import { ArrowUpRight, type LucideIcon } from 'lucide-react';

interface StatTileProps {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  /** Shown in the corner circle; clickable tiles without one show an arrow, `null` shows none. */
  icon?: LucideIcon | null;
  /** Small status dot before the label, e.g. `bg-red-500` for missed calls. */
  dotClassName?: string;
  /** Filled with the accent colour; use for one headline number per row. */
  featured?: boolean;
  /** `card` sits on the grey page panel, `inset` sits inside a white card. */
  surface?: 'card' | 'inset';
  size?: 'lg' | 'md';
  active?: boolean;
  onClick?: () => void;
  title?: string;
}

/** Big-number tile: label and corner icon on top, the value, then a short caption. */
export function StatTile({
  label,
  value,
  caption,
  icon: Icon,
  dotClassName,
  featured = false,
  surface = 'card',
  size = 'lg',
  active = false,
  onClick,
  title,
}: StatTileProps) {
  const CornerIcon = Icon === undefined && onClick ? ArrowUpRight : Icon;
  const base = featured
    ? 'bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white'
    : surface === 'card'
      ? 'bg-white dark:bg-gray-800'
      : 'bg-panel dark:bg-gray-900/60';
  const interactive = onClick
    ? featured || active
      ? 'cursor-pointer hover:brightness-105'
      : 'cursor-pointer hover:ring-1 hover:ring-gray-300 dark:hover:ring-gray-600'
    : '';

  const body = (
    <>
      <div className="flex items-start justify-between gap-3">
        <p
          className={`flex min-w-0 items-center gap-2 font-medium ${
            size === 'lg' ? 'text-base' : 'text-sm'
          } ${featured ? 'text-white' : 'text-gray-800 dark:text-gray-100'}`}
        >
          {dotClassName && (
            <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${dotClassName}`} />
          )}
          <span className="truncate">{label}</span>
        </p>
        {CornerIcon && (
          <span
            aria-hidden="true"
            className={`hidden shrink-0 items-center justify-center rounded-full sm:flex ${
              size === 'lg' ? 'h-10 w-10' : 'h-8 w-8'
            } ${
              featured
                ? 'bg-white text-primary-700'
                : 'border border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300'
            }`}
          >
            <CornerIcon size={size === 'lg' ? 18 : 15} />
          </span>
        )}
      </div>
      <p
        className={`font-semibold tracking-tight ${
          size === 'lg' ? 'mt-4 text-5xl' : 'mt-3 text-3xl'
        } ${featured ? 'text-white' : 'text-gray-900 dark:text-white'}`}
      >
        {value}
      </p>
      {caption && (
        <p
          className={`mt-auto pt-3 text-xs ${
            featured ? 'text-primary-50' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {caption}
        </p>
      )}
    </>
  );

  const className = `flex h-full w-full flex-col rounded-3xl text-left ${
    size === 'lg' ? 'p-5 xl:p-6' : 'p-4'
  } ${base} ${interactive} ${
    active ? 'ring-2 ring-primary-500' : ''
  } focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`;

  return onClick ? (
    <button type="button" onClick={onClick} title={title} className={className}>
      {body}
    </button>
  ) : (
    <div title={title} className={className}>
      {body}
    </div>
  );
}

/** Small rounded box for a number in a caption, like the reference's "5▲" chip. */
export function StatChip({ children, featured = false }: { children: ReactNode; featured?: boolean }) {
  return (
    <span
      className={`mr-1.5 inline-flex items-center rounded-md border px-1.5 py-px text-[11px] font-medium ${
        featured
          ? 'border-white/40 text-white'
          : 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-200'
      }`}
    >
      {children}
    </span>
  );
}
